import { useState, MouseEvent, useEffect } from 'react';
import MenuItem from '@mui/material/MenuItem';
import AgTypography from '../../../Styles/AgTypography';
import { AgColor } from '../../../Styles/AgColor';
import { Menu, Select, SelectChangeEvent } from '@mui/material';
import LanguageIcon from '@mui/icons-material/Language';

export interface LocaleOptionProps {
  locale: string;
}

const AgLanguageDropdown = (
  {
    localeOptions,
    useDropdown,
    expandToLeft,
    selected,
    setSelected,
    onChange,
    dataTestId
  }: {
    localeOptions: LocaleOptionProps[] | undefined;
    expandToLeft?: boolean,
    useDropdown?: boolean;
    setSelected?: (lang: string) => void;
    selected?: string;
    onChange?: Function;
    dataTestId?: string;
  }) => {
  const [lang, setLang] = useState<string>(
    localeOptions?.find((o) => o.locale === 'English')?.locale || localeOptions?.[0]?.locale || ''
  );
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  useEffect(() => {
    if (selected) setLang(selected);
  }, [selected]);

  const handleChange = (e: SelectChangeEvent) => {
    const locale = e.target.value;
    onChange?.(locale);
    setSelected?.(locale);
    setLang(locale);
  };

  const renderLocalesMenuItems = () => {
    return localeOptions?.length &&
    localeOptions?.map((item, index) => {
      return (
        <MenuItem
          key={index}
          value={useDropdown ? item.locale : undefined}
          style={{
            padding: '0',
            backgroundColor:
              item.locale === lang
                ? AgColor.secondaryLight2
                : AgColor.grey0
          }}
          onClick={() => {
            if (useDropdown) return;
            onChange?.(item.locale);
            setSelected?.(item.locale);
            setLang(item.locale);
            handleClose();
          }}
        >
          <AgTypography
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              width: '100%'
            }}
          >
            <AgTypography
              contentStyle={'AP.Paragraph.Small.SemiBold'}
              style={{
                color: useDropdown ? AgColor.grey6 : AgColor.secondaryDark,
                width: '100%',
                padding: '13px 16px'
              }}
              sx={{
                '&:hover': {
                  color: `${useDropdown ? AgColor.grey8 : AgColor.secondaryMain} !important` // MUI has style by default, !important is necessary here
                }
              }}
            >
              {item.locale}
            </AgTypography>
          </AgTypography>
        </MenuItem>
      );
    });
  };

  return (
    <AgTypography dataTestId={dataTestId}>
      <AgTypography
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '6px 16px',
          width: 'fit-content',
          cursor: 'pointer',
          color: AgColor.secondaryDark,
        }}
        onClick={(e: MouseEvent<HTMLElement>) => !useDropdown && handleClick(e)}
        sx={{
          cursor: 'pointer',
          '&:hover': {
            color: `${AgColor.secondaryMain} !important` // MUI has style by default, !important is necessary here
          }
        }}
      >
        {!useDropdown && <AgTypography
          style={{
            display: 'flex',
            alignItems: 'center',
            width: '16px',
            height: '16px',
            color: 'inherit'
          }}
        >
          <LanguageIcon
            style={{
              fontSize: '16px'
            }}
          />
        </AgTypography>}
        <AgTypography
          contentStyle={'AP.Paragraph.Small.Link'}
          style={{
            color: 'inherit',
            textDecoration: 'underline'
          }}
        >
          {!useDropdown ? <>{lang}</> :
            <Select
              sx={{ height: 40, color: AgColor.grey7 }}
              size='small'
              labelId='demo-select-small-label'
              id='demo-select-small'
              value={lang}
              onChange={(e: SelectChangeEvent) => handleChange(e)}
            >
              {renderLocalesMenuItems()}
            </Select>
          }
        </AgTypography>
      </AgTypography>
      {!useDropdown && <Menu
        id='demo-positioned-menu'
        aria-labelledby='demo-positioned-button'
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: expandToLeft ? 'top' : 'bottom',
          horizontal: expandToLeft ? 'left' : 'right'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
        sx={{
          width: '160px',
          ...(expandToLeft ? { left: '-4px' } : { top: '14px' }), // 2 scenarios here, 1 is on NavBar where locale on left, the other is locale on top right
          '& .MuiPaper-root': {
            boxShadow: '0px 1px 2px 0px #0000000D',
            border: `1px solid ${AgColor.grey3}`,
            width: '168px',
            borderRadius: '8px',
            padding: '4px 0'
          },
          '& .MuiMenu-list': {
            padding: '0'
          }
        }}
      >
        {renderLocalesMenuItems()}
      </Menu>
      }
    </AgTypography>
  );
};

export default AgLanguageDropdown;
